import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import config from '../../config';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import {
  hasAdminSessionAccess,
  isAdminPreviewMode,
  parseOptionalBoolValue,
} from '../../utils/admin-preview';
import { isRpaLoginTenant } from '../../screens/RpaLoginScreen/rpaLoginConfig';
import {
  resolveSelectedFormId,
  setSelectedFormIdInSession,
} from '../../utils/form-id';

const PSFA_USER_GUIDE_URL = 'https://psfa.dcat.site.grid.civilservice.gov.uk/';
const PSFA_PREPROD_USER_GUIDE_URL = 'https://psfa.dcat.site.preprod.grid.civilservice.gov.uk/';

export const getPsfaUserGuideUrl = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  apiUrl = String(config.apiUrl || ''),
) =>
  hostname.toLowerCase().includes('preprod') || apiUrl.toLowerCase().includes('preprod')
    ? PSFA_PREPROD_USER_GUIDE_URL
    : PSFA_USER_GUIDE_URL;

export default function ServiceNavigation() {
  const { isAuthenticated, signOut } = useAuthContext();
  const { tenant, formConfig } = useFormConfig();
  const location = useLocation();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const searchParams = new URLSearchParams(location?.search || '');
  const isMyFormsRoute =
    ['1', 'true', 'yes'].includes(String(searchParams.get('my_forms') || '').trim().toLowerCase());
  const selectedFormIdForShare = isMyFormsRoute ? undefined : selectedFormId;
  const requestedAdminView = parseOptionalBoolValue(
    searchParams.get('admin'),
  );
  const { data } = useFormData();

  if (!isAuthenticated) return null;

  const serviceName = formConfig?.formName || 'Service';
  const isRpaLogin = isRpaLoginTenant(tenant);
  const myFormsPath = `/${tenant}?my_forms=1&admin=0`;
  const adminPath = `/${tenant}?my_forms=1&admin=1`;
  const currentRole = String(data?.role || '').toLowerCase();
  const hasAdminAccess = hasAdminSessionAccess({
    hasAdminAccess: data?.hasAdminAccess,
    role: data?.role,
    sessionRoles: data?.sessionRoles,
    forms: data?.forms,
  });
  const isPreviewMode = isAdminPreviewMode({
    search: location?.search || '',
    selectedFormId,
    hasAdminAccess: data?.hasAdminAccess,
    role: data?.role,
    sessionRoles: data?.sessionRoles,
    forms: data?.forms,
    allowPendingAccessCheck: true,
  });
  const hasMyFormsAccess =
    data?.hasMyFormsAccess === true ||
    [currentRole, ...(Array.isArray(data?.sessionRoles) ? data.sessionRoles.map(String) : [])].some(
      (role) => String(role || '').trim().toLowerCase() !== 'admin' && String(role || '').trim().length > 0,
    ) ||
    (Array.isArray(data?.forms) &&
      data.forms.some((form) => String(form?.role || '').trim().toLowerCase() !== 'admin'));
  const showMyFormsTab = !isRpaLogin;
  const showAdminTab =
    !isRpaLogin &&
    (hasAdminAccess || (requestedAdminView === true && (data === undefined || data === null)));
  const serviceHomePath = isRpaLogin ? `/${tenant}` : myFormsPath;
  const hasOwnerRole =
    currentRole === 'owner' ||
    (Array.isArray(data?.sessionRoles) &&
      data.sessionRoles.some((role) => String(role || '').trim().toLowerCase() === 'owner')) ||
    (Array.isArray(data?.forms) &&
      data.forms.some((form) => String(form?.role || '').trim().toLowerCase() === 'owner'));
  const hasCurrentFormShareRole =
    Boolean(selectedFormIdForShare) &&
    (['owner', 'editor', 'approver'].includes(currentRole) ||
      (currentRole === 'admin' && !isPreviewMode));
  const canShare =
    !isRpaLogin && formConfig?.sharing !== false && (hasOwnerRole || hasCurrentFormShareRole);
  const sharePath = selectedFormIdForShare
    ? `/${tenant}/share?formId=${selectedFormIdForShare}`
    : `/${tenant}/share`;

  return (
    <nav className="govuk-service-navigation" aria-label="Service navigation">
      <div className="govuk-width-container">
        <div className="govuk-service-navigation__container">
          <span className="govuk-service-navigation__service-name">
            <Link
              className="govuk-service-navigation__link"
              to={serviceHomePath}
              onClick={() => setSelectedFormIdInSession(tenant, null)}
            >
              {serviceName}
            </Link>
          </span>
          <ul className="govuk-service-navigation__list">
            {showMyFormsTab && (
              <li className="govuk-service-navigation__item">
                <Link
                  className="govuk-service-navigation__link"
                  to={myFormsPath}
                  onClick={() => setSelectedFormIdInSession(tenant, null)}
                >
                  My Forms
                </Link>
              </li>
            )}
            {showAdminTab && (
              <li className="govuk-service-navigation__item">
                <Link
                  className="govuk-service-navigation__link"
                  to={adminPath}
                  onClick={() => setSelectedFormIdInSession(tenant, null)}
                >
                  Admin
                </Link>
              </li>
            )}
            {canShare && (
              <li className="govuk-service-navigation__item">
                <Link
                  className="govuk-service-navigation__link"
                  to={sharePath}
                  onClick={() => {
                    if (!selectedFormIdForShare) {
                      setSelectedFormIdInSession(tenant, null);
                    }
                  }}
                >
                  Share
                </Link>
              </li>
            )}
            {tenant === 'psfa' && (
              <li className="govuk-service-navigation__item">
                <a
                  className="govuk-service-navigation__link"
                  href={getPsfaUserGuideUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  User guide<span className="govuk-visually-hidden"> (opens in new tab)</span>
                </a>
              </li>
            )}
            <li className="govuk-service-navigation__item">
              <Link
                className="govuk-service-navigation__link"
                to={`/${tenant}/${config.urls.signin}?loggedOut=1`}
                onClick={(event) => {
                  event.preventDefault();
                  try {
                    const current = new URL(window.location.href);
                    current.searchParams.set('logout', '1');
                    window.history.replaceState({}, '', `${current.pathname}${current.search}${current.hash}`);
                  } catch (_err) {
                  }
                  signOut(undefined, {
                    onSettled: () => {
                      window.location.assign(`/${tenant}/${config.urls.signin}?loggedOut=1`);
                    },
                  });
                }}
              >
                Sign out
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}
